import { Query } from "@apollo/client/react/components";
import cn from "classnames";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { Fragment } from "react";

import { GET_CITIES } from "@api/queries/suggestions";
import { useStore } from "@hooks/useZustandProvider";
import busIcon from "@images/svg/bus_v4.svg";
import i from "@styles/icons.module.scss";
import Loading from "@ui-kit/Loading/Loading";

import s from "./Suggestions.module.scss";

const Suggestions = ({
  search = "",
  selectItem,
  selectedStop,
  closePopper,
  onlyCity = false,
  popularDestinations = [], // <-- cities with stops but in this case render only stops
  isRight = false,
}) => {
  const { cms } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { lang } = cms.page;
  const { locale } = useRouter();
  const { t } = useTranslation();

  const handleSelectItem = (item) => {
    selectItem(item);

    if (closePopper) closePopper();
  };

  return (
    <Query
      query={GET_CITIES}
      variables={{
        cityName: search,
        lang: isEditMode ? lang : locale,
      }}
      skip={search.length < 3}
    >
      {({ loading, error, data = { cities: [] } }) => {
        if (loading) {
          return (
            <div className={s.suggestions}>
              <div className={cn(s.triangleWrap, isRight && s.right)}>
                <div className={s.triangle} />
              </div>
              <div className={s.list}>
                <Loading className={s.loading} />
              </div>
            </div>
          );
        }

        return (
          <div className={cn(s.suggestions, onlyCity && s.onlyCity)}>
            {(!!data.cities.length || !!popularDestinations?.length) && (
              <div className={cn(s.triangleWrap, isRight && s.right)}>
                <div className={s.triangle} />
              </div>
            )}
            {data.cities.length ? (
              <div className={s.list}>
                {data.cities.map(({ CityId, CityName, BusStops }) => {
                  const { CountryName = "" } = BusStops[0];
                  return (
                    <Fragment key={CityId}>
                      <div
                        className={s.townCountry}
                        key={CityId}
                        onClick={
                          onlyCity
                            ? () => selectItem({ CityId, CityName })
                            : () => {}
                        }
                      >
                        <icon className={i.point} />
                        {t(CityName)}
                        <span>, {t(CountryName)}</span>
                      </div>

                      {!onlyCity &&
                        BusStops.map(
                          ({ BusStopId, BusStopName, CountryName }) => {
                            const icon = BusStopName.includes("airplane")
                              ? "airplane"
                              : "bus";
                            return (
                              <div
                                key={BusStopId}
                                className={s.item}
                                onClick={() => {
                                  handleSelectItem({
                                    BusStopId,
                                    BusStopName,
                                    CityName,
                                    CountryName,
                                  });
                                }}
                              >
                                {icon === "bus" ? (
                                  <Image
                                    className={s.busIcon}
                                    src={busIcon}
                                    alt={BusStopName}
                                    width={13}
                                    height={13}
                                  />
                                ) : (
                                  <icon className={i[icon]} />
                                )}
                                <span>{BusStopName}</span>
                              </div>
                            );
                          },
                        )}
                    </Fragment>
                  );
                })}
              </div>
            ) : popularDestinations.length ? (
              <div className={s.list}>
                {search.length >= 3 && (
                  <>
                    <div className={s.townCountry}>{t("search results")}</div>
                    <div className={cn(s.item, s.noMatch)}>
                      {t("no matches for")} <b>«{search}»</b>
                    </div>
                  </>
                )}
                <div className={s.townCountry}>{t("popular destinations")}</div>
                {popularDestinations.map(({ CityId, CityName, BusStops }) =>
                  BusStops.map(({ BusStopId, BusStopName, CountryName }) => {
                    const icon = BusStopName.includes("airplane")
                      ? "airplane"
                      : "bus";
                    return (
                      selectedStop !== BusStopId && (
                        <div
                          key={BusStopId}
                          className={s.item}
                          onClick={() =>
                            selectItem({
                              BusStopId,
                              BusStopName,
                              CityName,
                              CountryName,
                            })
                          }
                        >
                          {icon === "bus" ? (
                            <Image
                              className={s.busIcon}
                              src={busIcon}
                              alt={BusStopName}
                              width={13}
                              height={13}
                            />
                          ) : (
                            <icon className={i[icon]} />
                          )}
                          <span>{BusStopName}</span>
                        </div>
                      )
                    );
                  }),
                )}
              </div>
            ) : null}
          </div>
        );
      }}
    </Query>
  );
};

export default Suggestions;
